import * as React from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Truck } from "../../../types";
import { DeleteConfirmModal } from "./DeleteConfirmModal";
import { DirectionEditModal } from "./DirectionEditModal";
import { LicensePlateEditModal } from "./LicensePlateEditModal";


const ITEM_HEIGHT = 48;

interface ActionMenuProps {
  truck: Truck | Truck[];
  token?: string;
  onDirectionChange?: (truck: Truck, direction: "in" | "out") => void;
  onLicensePlateChange?: (truck: Truck, newLicensePlate: string) => void;
  onDelete?: (truck: Truck) => void;
  groupedTrucks?: Record<string, Truck[]>;
  context?: "main-table" | "details-modal";
}

export default function ActionMenu({
  truck,
  token,
  onDirectionChange,
  onLicensePlateChange,
  onDelete,
  groupedTrucks,
  context = "details-modal",
}: ActionMenuProps): React.JSX.Element {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [directionModalOpen, setDirectionModalOpen] = React.useState(false);
  const [licensePlateModalOpen, setLicensePlateModalOpen] =
    React.useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] =
    React.useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditDirection = () => {
    if (context === "main-table") {
      // For main table, open license plate edit modal
      setLicensePlateModalOpen(true);
    } else {
      // For details modal, open direction edit modal
      setDirectionModalOpen(true);
    }
    handleClose();
  };

  const handleEditLicensePlate = () => {
    setLicensePlateModalOpen(true);
    handleClose();
  };

  const handleDeleteEntry = () => {
    setDeleteConfirmModalOpen(true);
    handleClose();
  };

  const getMenuItems = () => {
    const items = [];

    if (context === "main-table") {
      items.push(
        <MenuItem key="license" onClick={handleEditDirection}>
          Edit license plate
        </MenuItem>
      );
    } else {
      items.push(
        <MenuItem key="direction" onClick={handleEditDirection}>
          Edit direction
        </MenuItem>
      );
      items.push(
        <MenuItem key="license" onClick={handleEditLicensePlate}>
          Edit license plate
        </MenuItem>
      );
    }

    items.push(
      <MenuItem key="delete" onClick={handleDeleteEntry}>
        {context === "main-table" ? "Delete truck" : "Delete entry"}
      </MenuItem>
    );

    return items;
  };

  return (
    <div>
      <IconButton
        aria-label="actions"
        id="action-button"
        aria-controls={open ? "action-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        sx={{ marginTop: 2, p: "4px" }}
      >
        <MoreHorizIcon />
      </IconButton>

      <Menu
        id="action-menu"
        MenuListProps={{
          "aria-labelledby": "action-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: ITEM_HEIGHT * 4.5,
            width: "20ch",
          },
        }}
      >
        {getMenuItems()}
      </Menu>

      <DirectionEditModal
        open={directionModalOpen}
        onClose={() => setDirectionModalOpen(false)}
        truck={truck}
        onDirectionChange={onDirectionChange}
      />

      <LicensePlateEditModal
        open={licensePlateModalOpen}
        onClose={() => setLicensePlateModalOpen(false)}
        truck={truck}
        onLicensePlateChange={onLicensePlateChange}
      />

      <DeleteConfirmModal
        open={deleteConfirmModalOpen}
        onClose={() => setDeleteConfirmModalOpen(false)}
        truck={truck}
        onDelete={onDelete}
        groupedTrucks={groupedTrucks}
        context={context}
      />
    </div>
  );
}
